<template>

  <!-- donut + legend; hover on a segment or a legend row shows it in the center -->
  <div class="row donut dash-donut">

    <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`" role="img" :aria-label="label">
      <circle :cx="c" :cy="c" :r="(outer + inner) / 2" fill="none" class="track" :stroke-width="outer - inner"/>
      <path v-for="s in segments" :key="s.key" :d="s.path" :class="['seg', s.slot, {dim: hover && hover != s.key}]"
        @mouseenter="hover = s.key" @mouseleave="hover = undefined">
        <title>{{ s.title }}: {{ format(s.value) }} ({{ s.pct }}%)</title>
      </path>
      <text :x="c" :y="c - 4" class="center value">{{ format(focus ? focus.value : total) }}</text>
      <text :x="c" :y="c + 14" class="center sub">{{ focus ? `${focus.title} · ${focus.pct}%` : totalLabel }}</text>
    </svg>

    <div class="column left legend">
      <div class="row ch item" v-for="s in segments" :key="s.key" :class="{dim: hover && hover != s.key}"
        @mouseenter="hover = s.key" @mouseleave="hover = undefined">
        <span class="dot" :class="s.slot"></span>
        <p class="name">{{ s.title }}</p>
        <p class="val">{{ format(s.value) }}</p>
        <p class="pct">{{ s.pct }}%</p>
      </div>
      <p class="micro" v-if="!segments.length">No data</p>
    </div>

  </div>

</template>
<script>
import { defineComponent } from 'vue';

// 7 categorical slots + gray "Other": a palette never cycles (dataviz rule)
const SLOTS = 7

export default defineComponent({
  props:{
    items: {type: Array, default: () => []}, // [{key, title, value}]
    format: {type: Function, default: v => v},
    label: String,
    totalLabel: {type: String, default: 'total'},
    size: {type: Number, default: 200},
  },
  data() {
    return {
      hover: undefined,
    }
  },
  computed: {
    c(){ return this.size / 2 },
    outer(){ return this.size / 2 - 2 },
    inner(){ return this.size / 2 - 26 },

    total(){ return this.items.reduce((s, p) => s + (p.value || 0), 0) },

    // the biggest 7 get colors, the rest fold into Other; colors follow
    // the entity (key order), not the rank, so a reorder does not repaint
    groups(){
      let list = this.items.filter(p => p.value > 0).sort((a, b) => b.value - a.value)
      let top = list.slice(0, SLOTS)
      let rest = list.slice(SLOTS)
      let byKey = [...top].sort((a, b) => String(a.key).localeCompare(String(b.key), undefined, {numeric: true}))
      let res = top.map(p => ({...p, slot: `s${byKey.indexOf(p) + 1}`}))
      if (rest.length) res.push({key: 'other', title: `Other (${rest.length})`, value: rest.reduce((s, p) => s + p.value, 0), slot: 'other'})
      return res
    },

    segments(){
      let total = this.total
      if (!total) return []
      let a = -Math.PI / 2
      return this.groups.map(p => {
        let sweep = p.value / total * Math.PI * 2
        let seg = {...p, pct: this.pct(p.value / total), path: this.arc(a, a + sweep)}
        a += sweep
        return seg
      })
    },

    focus(){ return this.segments.find(s => s.key == this.hover) },
  },
  methods: {
    pct(v){
      let p = v * 100
      return p >= 10 ? Math.round(p) : p.toFixed(1)
    },

    // ring segment from a0 to a1 (radians)
    arc(a0, a1){
      if (a1 - a0 >= Math.PI * 2 - 1e-6) a1 = a0 + Math.PI * 2 - 1e-4
      let {c, outer: R, inner: r} = this
      let large = a1 - a0 > Math.PI ? 1 : 0
      let p = (rad, ang) => `${c + rad * Math.cos(ang)} ${c + rad * Math.sin(ang)}`
      return `M ${p(R, a0)} A ${R} ${R} 0 ${large} 1 ${p(R, a1)} L ${p(r, a1)} A ${r} ${r} 0 ${large} 0 ${p(r, a0)} Z`
    },
  },
});

</script>
<style scoped>
/* categorical slots: validated with dataviz validate_palette.js, light on #fff, dark on #191919 */
.donut{
  --s1: #2a78d6; --s2: #eb6834; --s3: #1baf7a; --s4: #eda100;
  --s5: #e87ba4; --s6: #008300; --s7: #6250d6; --other: #a3a29c;
  --gap: var(--back);
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.s1{--c: var(--s1)} .s2{--c: var(--s2)} .s3{--c: var(--s3)} .s4{--c: var(--s4)}
.s5{--c: var(--s5)} .s6{--c: var(--s6)} .s7{--c: var(--s7)} .other{--c: var(--other)}

.track{ stroke: var(--g05); }
.seg{
  fill: var(--c);
  stroke: var(--gap);
  stroke-width: 2px;
  cursor: pointer;
  transition: opacity .15s;
}
.dim{ opacity: .35; }

.center{ text-anchor: middle; fill: var(--text); }
.center.value{ font-size: 18px; font-weight: 600; }
.center.sub{ font-size: 11px; fill: var(--g5); }

.legend{
  min-width: 240px;
  flex: 1;
}
.item{
  width: 100%;
  padding: 4px 6px;
  border-radius: 8px;
  cursor: default;
  transition: opacity .15s;
}
.item:hover{ background-color: var(--g05); }
.dot{
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 3px;
  background-color: var(--c);
}
.name{ font-size: 13px; flex: 1; }
.val{ font-size: 13px; font-variant-numeric: tabular-nums; margin-left: 12px; }
.pct{ font-size: 12px; color: var(--g5); width: 44px; text-align: right; font-variant-numeric: tabular-nums; }
</style>
<style>
/* не scoped: :global(body.dark) .x в scoped Vue компилирует в голый body.dark */
body.dark .dash-donut{
  --s1: #3987e5; --s2: #d95926; --s3: #199e70; --s4: #c98500;
  --s5: #d55181; --s6: #008300; --s7: #9085e9; --other: #5f5e5a;
}
</style>
